import { Fragment, useState, type ReactNode } from 'react';
import type { FleetMetricAggregate } from '@immo/api-contract';
import { Card } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { Approx, ClassSwatch, Eyebrow, MONO_STYLE, useMetricFormat } from './class-bar';
import {
  classValue,
  delta,
  isApproximate,
  isUnknown,
  niceMax,
  type ClassChoice,
  type MetricsView,
} from './metrics-math';
import { TOKEN_CLASS_LABEL, TOKEN_CLASSES } from './pricing';

/** One time slot on the x axis, labelled for the scale it belongs to. */
export type BucketInfo = {
  readonly label: string;
  readonly sub: string;
  readonly tip: string;
  readonly weekend: boolean;
};

export type SeriesRow = {
  readonly key: string;
  readonly total: FleetMetricAggregate;
  readonly previous: FleetMetricAggregate | null;
  readonly series: ReadonlyArray<FleetMetricAggregate | null>;
};

export type PanelData = SeriesRow & { readonly rows: ReadonlyArray<SeriesRow> };

type Hover = { readonly panel: string; readonly row: string; readonly bucket: number };

const LABEL_WIDTH = 132;
const RIGHT_WIDTH = 232;
const BAR_MAX_WIDTH = 24;

/** A titled card: eyebrow + hint over a divider, as the design's cards are built. */
export function MetricCard({
  title,
  hint,
  actions,
  testId,
  children,
}: {
  readonly title: string;
  readonly hint?: string | null;
  readonly actions?: ReactNode;
  readonly testId?: string;
  readonly children: ReactNode;
}) {
  return (
    <Card className="flex min-w-0 flex-col" data-testid={testId}>
      <div
        className="flex min-w-0 flex-wrap items-baseline"
        style={{
          gap: 'var(--space-2) var(--space-3)',
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--color-neutral-200)',
        }}
      >
        <Eyebrow>{title}</Eyebrow>
        {hint && <span className="text-sm text-text-tertiary">{hint}</span>}
        {actions && <span className="ml-auto">{actions}</span>}
      </div>
      <div className="min-w-0" style={{ padding: 'var(--space-4) var(--space-5)' }}>
        {children}
      </div>
    </Card>
  );
}

/**
 * The columns of one row. A bucket without a run draws nothing but the
 * baseline; a bucket whose runs all lack readable usage draws a dashed, empty
 * frame — never a zero-height bar; the hit area is the whole slot.
 */
function Columns({
  buckets,
  series,
  choice,
  max,
  height,
  strong,
  hot,
  onHover,
  testId,
}: {
  readonly buckets: ReadonlyArray<BucketInfo>;
  readonly series: ReadonlyArray<FleetMetricAggregate | null>;
  readonly choice: ClassChoice;
  readonly max: number;
  readonly height: number;
  readonly strong: boolean;
  readonly hot: number | null;
  readonly onHover: (bucket: number | null) => void;
  readonly testId: string;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');

  return (
    <div
      data-testid={testId}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
        gap: 2,
        height,
        borderBottom: '1px solid var(--color-neutral-200)',
        minWidth: 0,
      }}
    >
      {buckets.map((bucket, index) => {
        const aggregate = series[index] ?? null;
        const value = classValue(aggregate, choice);
        const isHot = hot === index;
        const barHeight = value > 0 ? Math.max(2, Math.round((value / max) * height)) : 0;

        return (
          <div
            key={index}
            onMouseEnter={() => onHover(index)}
            onMouseLeave={() => onHover(null)}
            style={{
              position: 'relative',
              height: '100%',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              background: isHot ? 'var(--color-hover-bg)' : bucket.weekend ? 'var(--bg-subtle)' : 'transparent',
              borderRadius: '2px 2px 0 0',
            }}
          >
            {isUnknown(aggregate) ? (
              <span
                data-unknown
                title={t('tip-unknown')}
                style={{
                  width: '70%',
                  maxWidth: BAR_MAX_WIDTH,
                  height: Math.max(8, Math.round(height * 0.55)),
                  boxSizing: 'border-box',
                  border: '1px dashed var(--color-neutral-400)',
                  borderBottom: 'none',
                  borderRadius: '4px 4px 0 0',
                }}
              />
            ) : barHeight > 0 ? (
              <span
                data-bar
                style={{
                  width: '70%',
                  maxWidth: BAR_MAX_WIDTH,
                  height: barHeight,
                  background: isHot
                    ? 'var(--color-info-700)'
                    : strong
                      ? 'var(--color-info-500)'
                      : 'var(--color-info-400)',
                  borderRadius: barHeight > 4 ? '4px 4px 0 0' : '1px 1px 0 0',
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Axis({ buckets }: { readonly buckets: ReadonlyArray<BucketInfo> }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
        gap: 2,
        minHeight: 28,
        minWidth: 0,
      }}
    >
      {buckets.map((bucket, index) => (
        <div
          key={index}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 3, minWidth: 0 }}
        >
          <span className="text-xs text-text-tertiary" style={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}>
            {bucket.label}
          </span>
          {bucket.sub !== '' && (
            <span style={{ ...MONO_STYLE, fontSize: '10px', color: 'var(--text-placeholder)' }}>{bucket.sub}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Every bar's tooltip shows the four classes one by one; the sum stands under them, never alone. */
export function BucketTooltip({
  title,
  sub,
  aggregate,
  choice,
  side,
}: {
  readonly title: string;
  readonly sub: string;
  readonly aggregate: FleetMetricAggregate;
  readonly choice: ClassChoice;
  readonly side: 'right' | 'left';
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const unknown = isUnknown(aggregate);

  return (
    <div
      role="tooltip"
      data-testid="bucket-tooltip"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        [side === 'right' ? 'left' : 'right']: -4,
        zIndex: 30,
        width: 236,
        background: 'var(--bg-surface)',
        border: '1px solid var(--color-neutral-200)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        padding: 'var(--space-3)',
        pointerEvents: 'none',
        textAlign: 'left',
      }}
    >
      <div style={{ fontSize: 'var(--text-base-sm)', fontWeight: 'var(--font-weight-semibold)', whiteSpace: 'nowrap' }}>
        {title}
      </div>
      <div
        style={{
          ...MONO_STYLE,
          fontSize: 'var(--text-xs)',
          color: 'var(--text-tertiary)',
          marginBottom: 'var(--space-2)',
        }}
      >
        {sub}
      </div>
      {unknown ? (
        <div className="text-sm text-text-secondary" style={{ lineHeight: 1.45 }}>
          <div style={{ ...MONO_STYLE, color: 'var(--text-primary)' }}>—</div>
          {t('tip-unknown')}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '12px 1fr auto',
            columnGap: 'var(--space-2)',
            rowGap: 3,
            alignItems: 'center',
          }}
        >
          {TOKEN_CLASSES.map((key) => {
            const chosen = choice === key;
            const color = chosen ? 'var(--text-primary)' : 'var(--text-secondary)';
            const weight = chosen ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)';

            return (
              <Fragment key={key}>
                <ClassSwatch tokenClass={key} />
                <span style={{ ...MONO_STYLE, fontSize: 'var(--text-sm)', color, fontWeight: weight }}>
                  {TOKEN_CLASS_LABEL[key]}
                </span>
                <span style={{ ...MONO_STYLE, fontSize: 'var(--text-sm)', color, textAlign: 'right' }}>
                  {format.tokens(classValue(aggregate, key))}
                </span>
              </Fragment>
            );
          })}
          <span />
          <span
            className="text-sm text-text-tertiary"
            style={{ borderTop: '1px solid var(--color-neutral-100)', paddingTop: 3 }}
          >
            {t('sum')}
          </span>
          <span style={{ borderTop: '1px solid var(--color-neutral-100)', paddingTop: 3, textAlign: 'right' }}>
            <Approx show={isApproximate(aggregate)} title={t('approx-title')} />
            <span
              style={{
                ...MONO_STYLE,
                fontSize: 'var(--text-sm)',
                color: choice === 'all' ? 'var(--text-primary)' : 'var(--text-tertiary)',
              }}
            >
              {format.tokens(classValue(aggregate, 'all'))}
            </span>
          </span>
        </div>
      )}
      <div
        className="flex flex-wrap text-sm text-text-tertiary"
        style={{ marginTop: 'var(--space-2)', gap: 'var(--space-2)' }}
      >
        <span>{t('tip-runs', { count: aggregate.runs })}</span>
        {isApproximate(aggregate) && !unknown && <span>· {t('tip-unparsed', { count: aggregate.unparsedRuns })}</span>}
        <span>· {format.duration(aggregate.durationMs)}</span>
      </div>
    </div>
  );
}

/** Total · share bar · change — right of every row. */
function RowRight({
  row,
  share,
  choice,
  hasPrevious,
  strong,
}: {
  readonly row: SeriesRow;
  readonly share: number | null;
  readonly choice: ClassChoice;
  readonly hasPrevious: boolean;
  readonly strong: boolean;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const unknown = isUnknown(row.total);
  const value = classValue(row.total, choice);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '84px 92px 48px',
        alignItems: 'center',
        gap: 4,
        minWidth: 0,
      }}
    >
      <span style={{ textAlign: 'right', whiteSpace: 'nowrap', lineHeight: 1 }} data-testid="row-total">
        <Approx show={isApproximate(row.total) && !unknown} title={t('approx-title')} />
        <span
          style={{
            ...MONO_STYLE,
            fontSize: strong ? 'var(--text-base-sm)' : 'var(--text-sm)',
            fontWeight: strong ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
            color: strong ? 'var(--text-primary)' : 'var(--text-secondary)',
          }}
        >
          {unknown ? '—' : format.tokensShort(value)}
        </span>
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, lineHeight: 1 }}>
        {share !== null && (
          <>
            <span
              style={{
                width: 48,
                height: 6,
                borderRadius: 3,
                background: 'var(--color-neutral-100)',
                flex: 'none',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: `${Math.min(100, share * 100)}%`,
                  background: 'var(--color-neutral-400)',
                }}
              />
            </span>
            <span style={{ ...MONO_STYLE, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
              {format.percentWhole(share)}
            </span>
          </>
        )}
      </span>
      <span style={{ textAlign: 'right', lineHeight: 1 }}>
        {hasPrevious && !unknown && row.previous !== null && (
          <span style={{ ...MONO_STYLE, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            {format.delta(delta(value, classValue(row.previous, choice)))}
          </span>
        )}
      </span>
    </div>
  );
}

function Panel({
  panel,
  buckets,
  choice,
  fleetValue,
  rowMax,
  totalMax,
  expectedRows,
  previousLabel,
  compact,
  hover,
  onHover,
}: {
  readonly panel: PanelData;
  readonly buckets: ReadonlyArray<BucketInfo>;
  readonly choice: ClassChoice;
  readonly fleetValue: number;
  readonly rowMax: number;
  readonly totalMax: number;
  readonly expectedRows: ReadonlyArray<string>;
  readonly previousLabel: string | null;
  readonly compact: boolean;
  readonly hover: Hover | null;
  readonly onHover: (hover: Hover | null) => void;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const panelValue = classValue(panel.total, choice);
  const hasPrevious = panel.previous !== null && previousLabel !== null;
  const missing = expectedRows.filter((key) => !panel.rows.some((row) => row.key === key));
  const grid = compact
    ? { display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', rowGap: 2 }
    : {
        display: 'grid',
        gridTemplateColumns: `${LABEL_WIDTH}px minmax(0,1fr) ${RIGHT_WIDTH}px`,
        columnGap: 'var(--space-4)',
        alignItems: 'end',
      };

  const line = (row: SeriesRow, isSum: boolean) => {
    const rowKey = isSum ? '*' : row.key;
    const hot = hover !== null && hover.panel === panel.key && hover.row === rowKey ? hover.bucket : null;
    const share = isSum ? null : panelValue > 0 ? classValue(row.total, choice) / panelValue : 0;
    const hotAggregate = hot === null ? null : (row.series[hot] ?? null);
    const hotBucket = hot === null ? undefined : buckets[hot];
    const chart = (
      <div style={{ position: 'relative', minWidth: 0 }}>
        <Columns
          buckets={buckets}
          series={row.series}
          choice={choice}
          max={isSum ? totalMax : rowMax}
          height={isSum ? (compact ? 56 : 64) : compact ? 24 : 26}
          strong={isSum}
          hot={hot}
          onHover={(bucket) => onHover(bucket === null ? null : { panel: panel.key, row: rowKey, bucket })}
          testId={`columns-${panel.key}-${rowKey}`}
        />
        {hot !== null && hotAggregate !== null && hotBucket !== undefined && (
          <div
            style={{
              position: 'absolute',
              left: `${((hot + 0.5) / buckets.length) * 100}%`,
              top: 0,
              width: 0,
              height: 0,
            }}
          >
            <BucketTooltip
              title={hotBucket.tip}
              sub={isSum ? panel.key : `${panel.key} · ${row.key}`}
              aggregate={hotAggregate}
              choice={choice}
              side={hot < buckets.length * 0.6 ? 'right' : 'left'}
            />
          </div>
        )}
      </div>
    );
    const label = isSum ? t('sum') : row.key;

    if (compact) {
      const unknown = isUnknown(row.total);

      return (
        <div
          key={rowKey}
          data-testid={`row-${panel.key}-${rowKey}`}
          className="flex flex-col"
          style={{ gap: 2, marginTop: isSum ? 0 : 'var(--space-2)' }}
        >
          <div className="flex min-w-0 items-baseline" style={{ gap: 'var(--space-2)' }}>
            <span
              className="min-w-0 truncate text-sm"
              style={{
                fontFamily: isSum ? 'var(--font-sans)' : 'var(--font-mono)',
                color: isSum ? 'var(--text-tertiary)' : 'var(--text-secondary)',
              }}
            >
              {label}
            </span>
            <span className="flex-1" />
            <Approx show={isApproximate(row.total) && !unknown} title={t('approx-title')} />
            <span
              style={{
                ...MONO_STYLE,
                fontSize: 'var(--text-sm)',
                color: isSum ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: isSum ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
              }}
            >
              {unknown ? '—' : format.tokensShort(classValue(row.total, choice))}
            </span>
            {share !== null && (
              <span style={{ ...MONO_STYLE, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                {format.percentWhole(share)}
              </span>
            )}
          </div>
          {chart}
        </div>
      );
    }

    return (
      <div key={rowKey} data-testid={`row-${panel.key}-${rowKey}`} style={{ ...grid, marginTop: isSum ? 0 : 6 }}>
        <div className="flex min-w-0 flex-col justify-end" style={{ paddingBottom: 4 }}>
          {isSum ? (
            <>
              <span style={{ ...MONO_STYLE, fontSize: '10px', color: 'var(--text-placeholder)' }}>
                {format.tokensShort(totalMax)}
              </span>
              <span className="text-sm text-text-tertiary">{label}</span>
            </>
          ) : (
            <span
              className="min-w-0 truncate"
              title={label}
              style={{ ...MONO_STYLE, fontSize: 'var(--text-base-sm)', color: 'var(--text-secondary)' }}
            >
              {label}
            </span>
          )}
        </div>
        {chart}
        <div style={{ paddingBottom: 4 }}>
          <RowRight row={row} share={share} choice={choice} hasPrevious={hasPrevious} strong={isSum} />
        </div>
      </div>
    );
  };

  return (
    <div data-testid={`panel-${panel.key}`} className="min-w-0">
      <div
        className="flex min-w-0 flex-wrap items-baseline"
        style={{ gap: 'var(--space-1) var(--space-3)', marginBottom: 'var(--space-2)' }}
      >
        <span
          className="min-w-0 truncate"
          style={{ ...MONO_STYLE, fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)' }}
        >
          {panel.key}
        </span>
        <span className="flex-1" />
        <span className="text-sm text-text-tertiary" style={{ whiteSpace: 'nowrap' }}>
          {t('share-of-fleet', { share: format.percentWhole(fleetValue > 0 ? panelValue / fleetValue : 0) })}
          {hasPrevious && !compact && panel.previous !== null && (
            <>
              {' · '}
              <span style={{ ...MONO_STYLE, color: 'var(--text-secondary)' }}>
                {format.delta(delta(panelValue, classValue(panel.previous, choice)))}
              </span>{' '}
              {previousLabel}
            </>
          )}
        </span>
      </div>
      {line(panel, true)}
      {panel.rows.map((row) => line(row, false))}
      <div style={compact ? {} : grid}>
        {!compact && <span />}
        <Axis buckets={buckets} />
        {!compact && <span />}
      </div>
      {missing.length > 0 && (
        <div
          className="text-sm text-text-tertiary"
          style={{ marginTop: 2, paddingLeft: compact ? 0 : LABEL_WIDTH + 16 }}
        >
          {t('no-run-for', { list: missing.join(', ') })}
        </div>
      )}
    </div>
  );
}

const SERIES_TITLE: Readonly<Record<MetricsView, string>> = {
  machine: 'series-machine',
  agent: 'series-agent',
  model: 'series-model',
};

const ROWS_HINT: Readonly<Record<MetricsView, string>> = {
  machine: 'rows-agents',
  agent: 'rows-machines',
  model: 'rows-agents',
};

/**
 * Every panel of a view. ONE y scale for every row of every panel and a second
 * for every panel sum — otherwise small multiples lie: half a bar must mean
 * the same thing everywhere.
 */
export function PanelsCard({
  view,
  panels,
  buckets,
  choice,
  total,
  expectedRows,
  previousLabel,
  compact,
}: {
  readonly view: MetricsView;
  readonly panels: ReadonlyArray<PanelData>;
  readonly buckets: ReadonlyArray<BucketInfo>;
  readonly choice: ClassChoice;
  readonly total: FleetMetricAggregate;
  readonly expectedRows: ReadonlyArray<string>;
  readonly previousLabel: string | null;
  readonly compact: boolean;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const [hover, setHover] = useState<Hover | null>(null);
  const bucketMax = (rows: ReadonlyArray<SeriesRow>) =>
    rows.reduce((max, row) => row.series.reduce((inner, entry) => Math.max(inner, classValue(entry, choice)), max), 0);
  const totalMax = niceMax(bucketMax(panels));
  const rowMax = niceMax(bucketMax(panels.flatMap((panel) => panel.rows)));
  const allTokens = classValue(total, 'all');
  const cacheReadShare = allTokens > 0 ? classValue(total, 'cacheRead') / allTokens : 0;

  return (
    <MetricCard
      title={t(SERIES_TITLE[view])}
      hint={compact ? null : `${t(ROWS_HINT[view])} · ${t('one-scale')}`}
      testId="panels-card"
    >
      <p className="text-sm text-text-tertiary" style={{ margin: '0 0 var(--space-4)', lineHeight: 1.5 }}>
        {choice === 'all'
          ? t('hint-all', { share: format.percent(cacheReadShare) })
          : t('hint-class', { class: TOKEN_CLASS_LABEL[choice] })}
      </p>
      <div className="flex flex-col" style={{ gap: compact ? 'var(--space-5)' : 'var(--space-6)' }}>
        {panels.length === 0 && <div className="text-sm text-text-tertiary">{t('no-run-selected')}</div>}
        {panels.map((panel, index) => (
          <div
            key={panel.key}
            style={{
              borderTop: index > 0 ? '1px solid var(--color-neutral-100)' : 'none',
              paddingTop: index > 0 ? (compact ? 'var(--space-4)' : 'var(--space-5)') : 0,
            }}
          >
            <Panel
              panel={panel}
              buckets={buckets}
              choice={choice}
              fleetValue={classValue(total, choice)}
              rowMax={rowMax}
              totalMax={totalMax}
              expectedRows={expectedRows}
              previousLabel={previousLabel}
              compact={compact}
              hover={hover}
              onHover={setHover}
            />
          </div>
        ))}
      </div>
    </MetricCard>
  );
}
